import { useState } from 'react';
import { motion } from 'framer-motion';
import { Settings, Save, Server, MessageCircle, Image } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';

const AdminSettings = () => {
  const { token } = useAuth();
  const { settings, updateSettings } = useSettings();
  const [formData, setFormData] = useState({
    server_name: settings.server_name || '',
    server_ip: settings.server_ip || '',
    discord_link: settings.discord_link || '',
    discord_widget_id: settings.discord_widget_id || '',
    logo_url: settings.logo_url || '',
    description: settings.description || ''
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await updateSettings(formData, token);
      toast.success('Paramètres sauvegardés avec succès !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-2xl text-white uppercase tracking-wider flex items-center gap-2">
          <Settings className="w-6 h-6 text-violet-400" />
          Paramètres
        </h1>
        <p className="text-gray-400">Configuration générale du site</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Server Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-xl p-6"
        >
          <h2 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-6 flex items-center gap-2">
            <Server className="w-5 h-5 text-violet-400" />
            Informations du serveur
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="server_name" className="text-gray-300 mb-2 block">
                Nom du serveur
              </Label>
              <Input
                id="server_name"
                name="server_name"
                value={formData.server_name}
                onChange={handleChange}
                placeholder="mcommunity"
                className="bg-black/50 border-white/10 focus:border-violet-500 text-white"
                data-testid="input-server-name"
              />
            </div>
            
            <div>
              <Label htmlFor="server_ip" className="text-gray-300 mb-2 block">
                Adresse du serveur FiveM
              </Label>
              <Input
                id="server_ip"
                name="server_ip"
                value={formData.server_ip}
                onChange={handleChange}
                placeholder="cfx.re/join/xxxxx"
                className="bg-black/50 border-white/10 focus:border-violet-500 text-white"
                data-testid="input-server-ip"
              />
              <p className="text-gray-500 text-xs mt-1">
                Format: cfx.re/join/CODE ou IP:PORT
              </p>
            </div>
            
            <div>
              <Label htmlFor="description" className="text-gray-300 mb-2 block">
                Description
              </Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Description du serveur..."
                rows={3}
                className="bg-black/50 border-white/10 focus:border-violet-500 text-white resize-none"
                data-testid="input-description"
              />
            </div>
          </div>
        </motion.div>

        {/* Discord */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-xl p-6"
        >
          <h2 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-6 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#5865F2]" />
            Discord
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="discord_link" className="text-gray-300 mb-2 block">
                Lien d'invitation Discord
              </Label>
              <Input
                id="discord_link"
                name="discord_link"
                value={formData.discord_link}
                onChange={handleChange}
                placeholder="https://discord.gg/xxxxx"
                className="bg-black/50 border-white/10 focus:border-violet-500 text-white"
                data-testid="input-discord-link"
              />
            </div>
            
            <div>
              <Label htmlFor="discord_widget_id" className="text-gray-300 mb-2 block">
                ID du widget Discord (optionnel)
              </Label>
              <Input
                id="discord_widget_id"
                name="discord_widget_id"
                value={formData.discord_widget_id}
                onChange={handleChange}
                placeholder="123456789012345678"
                className="bg-black/50 border-white/10 focus:border-violet-500 text-white"
                data-testid="input-discord-widget"
              />
            </div>
          </div>
        </motion.div>

        {/* Branding */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass rounded-xl p-6"
        >
          <h2 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-6 flex items-center gap-2">
            <Image className="w-5 h-5 text-green-400" />
            Branding
          </h2>
          
          <div>
            <Label htmlFor="logo_url" className="text-gray-300 mb-2 block">
              URL du logo
            </Label>
            <Input
              id="logo_url"
              name="logo_url"
              value={formData.logo_url}
              onChange={handleChange}
              placeholder="https://example.com/logo.png"
              className="bg-black/50 border-white/10 focus:border-violet-500 text-white"
              data-testid="input-logo-url"
            />
            
            {formData.logo_url && (
              <div className="mt-4 p-4 bg-black/30 rounded-lg">
                <p className="text-gray-400 text-sm mb-2">Aperçu:</p>
                <img 
                  src={formData.logo_url} 
                  alt="Logo preview"
                  className="h-12 w-auto"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
          </div>
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Button
            type="submit"
            disabled={saving}
            className="w-full bg-violet-600 hover:bg-violet-500 text-white font-heading font-bold uppercase tracking-wider py-6"
            data-testid="save-settings-btn"
          >
            {saving ? (
              'Sauvegarde en cours...'
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Sauvegarder les paramètres
              </>
            )}
          </Button>
        </motion.div>
      </form>
    </div>
  );
};

export default AdminSettings;
